import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { CounterTabletModal, DeskCommunicationModal } from "modules/AppointmentDetails/components/DeskCommunication/components";
import { isCarReadyOrQualityCheck } from "utils/appointmentUtils";

import type { Appointment } from "models";

type DeskCommunicationProps = {
  appointment: Appointment;
  showDeskComm?: boolean;
  hideDeskButton?: boolean;
  onClose?: () => void;
};

export const DeskCommunication = ({ appointment, showDeskComm, hideDeskButton, onClose }: DeskCommunicationProps) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCounterTabletModal, setShowCounterTabletModal] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);

  const { t } = useTranslation();
  const { user } = usePermanentRealTimeUser();
  const isDeskCheckOut = isCarReadyOrQualityCheck(appointment.appointment_status_identifier);

  const handleTabletClick = () => setShowConfirmModal(true);

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    onClose?.();
  };

  const handleConfirmDeskCommunication = () => {
    handleCancelConfirm();
    setShowCommunicationModal(true);
  };

  const handleCommunicationSend = async () => {
    setShowCommunicationModal(false);
    setShowCounterTabletModal(true);
  };

  const handleCounterTabletClose = () => setShowCounterTabletModal(false);

  const handleCloseCommunicationModal = () => {
    setShowCommunicationModal(false);
  };

  const renderTabletConfirmModal = () => {
    if (!showConfirmModal && !(showDeskComm && hideDeskButton)) return null;

    const confirmMessage = isDeskCheckOut
      ? t("v8_action_will_display_desk_checkout_on_counter_tablet", "This action will display the desk check-out on the counter tablet. Do you want to proceed?")
      : t("v8_action_will_display_desk_checkin_on_counter_tablet", "This action will display the desk check-in on the counter tablet. Do you want to proceed?");

    if (user?.counter_tablet_key)
      return (
        <CustomConfirm
          customTitle={isDeskCheckOut ? t("v8_desk_check_out", "Desk check-out") : t("v8_desk_check_in", "Desk check-in")}
          type={CUSTOM_CONFIRM_TYPES.Warning}
          handleConfirm={handleConfirmDeskCommunication}
          isOpen={showConfirmModal || Boolean(showDeskComm && hideDeskButton)}
          confirmMsg={confirmMessage}
          confirmButtonText={t("v8_confirm", "Confirm")}
          handleCancel={handleCancelConfirm}
        />
      );

    return (
      <CustomConfirm
        customTitle={t("v8_counter_tablet_missing", "Counter tablet missing")}
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={showConfirmModal}
        handleCancel={handleCancelConfirm}
        confirmMsg={t("v8_please_connect_your_profile_with_counter_tablet", "Please connect your profile with a counter tablet")}
        cancelButtonText={t("v8_close", "Close")}
      />
    );
  };

  return (
    <>
      {!hideDeskButton && (
        <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.DeskCheckin}>
          <Button className="-appointment-status" size="small" onClick={handleTabletClick}>
            <Icon className="tablet screen" />
          </Button>
        </Can>
      )}

      {renderTabletConfirmModal()}
      {showCounterTabletModal && <CounterTabletModal appointment={appointment} onClose={handleCounterTabletClose} />}
      {showCommunicationModal && <DeskCommunicationModal appointment={appointment} onSend={handleCommunicationSend} onClose={handleCloseCommunicationModal} />}
    </>
  );
};
